/**
 * Rounds an amount to whole cents, half away from zero. Plain `Math.round(x * 100)` misreads exact
 * half-cent ties because of binary floating point (1.005 * 100 is 100.49999999999999, so it rounds
 * down), and a different answer on server and browser would show as a one-cent mismatch.
 */
export function roundToCents(value: number): number {
  const cents = Number((Math.abs(value) * 100).toPrecision(15));
  return Math.sign(value) * Math.round(cents);
}

/** An amount rounded to two decimals. */
export const roundMoney = (value: number): number => roundToCents(value) / 100;

/**
 * Formats an integer minor amount (cents) to a two-decimal string without floating-point division.
 * e.g. 1999 -> "19.99", 5 -> "0.05", 0 -> "0.00", -50 -> "-0.50".
 */
export function formatMinor(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(Math.round(cents));
  const whole = Math.floor(abs / 100);
  const fraction = String(abs % 100).padStart(2, "0");
  return `${sign}${whole}.${fraction}`;
}

/**
 * Formats a money amount (number) to a two-decimal string without floating-point division drift.
 */
export const formatMoney = (value: number): string => formatMinor(roundToCents(value));
